import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

interface VendorNavCountsContextType {
  rfqCount: number;
  bidsCount: number;
  activeJobsCount: number;
  loading: boolean;
  refreshCounts: () => void;
}

const VendorNavCountsContext = createContext<VendorNavCountsContextType | undefined>(undefined);

export const VendorNavCountsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState({
    rfqCount: 0,
    bidsCount: 0,
    activeJobsCount: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchCounts = useCallback(async () => {
    if (!user || user.role !== 'vendor') {
      setLoading(false);
      return;
    }

    try {
      // Get user's profile and org role
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      const orgId = profile?.organization_id;

      const { data: orgRole } = await supabase
        .rpc('get_user_org_role', {
          _user_id: user.id,
          _org_id: orgId
        } as any);

      // Get org user IDs for owners/super_users
      let orgUserIds: string[] = [user.id];
      if ((orgRole === 'owner' || orgRole === 'super_user') && orgId) {
        const { data: orgUsers } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('organization_id', orgId);
        if (orgUsers && orgUsers.length > 0) {
          orgUserIds = orgUsers.map(u => u.user_id);
        }
      }

      // Fetch all counts in parallel
      const [rfqResult, bidsResult, jobsResult] = await Promise.all([
        // Count open RFQs excluding user's bids and declines
        (async () => {
          const { data: openRfqs } = await supabase
            .from('rfqs')
            .select('id')
            .eq('status', 'open');

          if (!openRfqs || openRfqs.length === 0) return 0;

          const rfqIds = openRfqs.map(r => r.id);

          const { data: userBids } = await supabase
            .from('bids')
            .select('rfq_id')
            .eq('vendor_user_id', user.id)
            .in('rfq_id', rfqIds);

          const biddedRfqIds = new Set(userBids?.map(b => b.rfq_id) || []);

          const { data: declines } = await supabase
            .from('rfq_declines')
            .select('rfq_id')
            .eq('vendor_user_id', user.id);

          const declinedRfqIds = new Set(declines?.map(d => d.rfq_id) || []);

          const availableCount = rfqIds.filter(
            id => !biddedRfqIds.has(id) && !declinedRfqIds.has(id)
          ).length;

          return availableCount;
        })(),

        // Count bids where job is NOT in awarded/in_progress/etc statuses
        (async () => {
          const excludedJobStatuses = [
            'awarded',
            'in_progress',
            'completed_pending_review',
            'accepted',
            'rejected',
            'disputed',
            'closed'
          ];

          let query = supabase
            .from('bids')
            .select(`
              id,
              rfqs!inner(
                job:jobs!inner(status)
              )
            `);

          if (orgRole === 'member') {
            query = query.eq('vendor_user_id', user.id);
          } else {
            query = query.in('vendor_user_id', orgUserIds);
          }

          const { data: bidsData } = await query;

          const activeBids = (bidsData || []).filter((bid: any) => {
            const jobStatus = bid.rfqs?.job?.status;
            return jobStatus && !excludedJobStatuses.includes(jobStatus);
          });

          return activeBids.length;
        })(),

        // Count active jobs
        (async () => {
          const activeStatuses = [
            'awarded',
            'in_progress',
            'completed_pending_review',
            'accepted',
            'rejected',
            'disputed',
            'closed'
          ];

          let query = supabase
            .from('jobs')
            .select(`
              id,
              awards!inner(
                bids!inner(vendor_user_id)
              )
            `)
            .eq('is_deleted', false)
            .in('status', activeStatuses);

          if (orgRole === 'member') {
            query = query.eq('awards.bids.vendor_user_id', user.id);
          } else {
            query = query.in('awards.bids.vendor_user_id', orgUserIds);
          }

          const { data: jobsData } = await query;

          return jobsData?.length || 0;
        })(),
      ]);

      setCounts({
        rfqCount: rfqResult,
        bidsCount: bidsResult,
        activeJobsCount: jobsResult,
      });
    } catch (error) {
      console.error('Error fetching vendor nav counts:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCounts();

    // Refresh counts every 60 seconds
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const refreshCounts = useCallback(() => {
    fetchCounts();
  }, [fetchCounts]);

  return (
    <VendorNavCountsContext.Provider value={{ ...counts, loading, refreshCounts }}>
      {children}
    </VendorNavCountsContext.Provider>
  );
};

export const useVendorNavCounts = (): VendorNavCountsContextType => {
  const context = useContext(VendorNavCountsContext);
  if (context === undefined) {
    // Return default values if used outside provider (for non-vendor users)
    return {
      rfqCount: 0,
      bidsCount: 0,
      activeJobsCount: 0,
      loading: false,
      refreshCounts: () => {},
    };
  }
  return context;
};
